<script lang="ts">
  import { game } from '$stores/game.svelte';
  import { scoreboard, fmtTime } from '$stores/scoreboard.svelte';
  import { onMount } from 'svelte';

  onMount(() => {
    scoreboard.start();
    return () => scoreboard.stop();
  });

  function back() {
    scoreboard.stop();
    game.goto(game.scoreboardOrigin || 'intro');
  }
</script>

<section class="scoreboard">
  <div class="sb-header">
    <button class="sb-back icon-btn" aria-label="Back" onclick={back}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <div class="sb-title">SCOREBOARD</div>
    <div class="sb-live"><span class="sb-live-dot"></span> LIVE</div>
  </div>

  <div class="sb-top3-section">
    <div class="sb-section-title">
      <span>TOP 3 TODAY</span>
      <span class="sb-side">last 24h</span>
    </div>
    <div class="sb-podium">
      {#each scoreboard.top3 as e, i (e.id)}
        <div class="sb-podium-card rank-{i + 1}">
          <div class="sb-rank-num">{i + 1}</div>
          <div class="sb-pair-stack">
            <div class="sb-pair-handles">
              {e.pair[0]}<span class="sb-amp">×</span>{e.pair[1]}
            </div>
            <div class="sb-pair-meta">
              <span class="sb-meta-cat">{e.cat}</span>
              <span>{fmtTime(e.minsAgo)}</span>
            </div>
          </div>
          <div class="sb-pct">{e.pct}%</div>
        </div>
      {/each}
    </div>
  </div>

  <div class="sb-feed-section">
    <div class="sb-section-title">
      <span>FROM YOUR ORBIT</span>
      <span class="sb-side">friends &amp; friends-of-friends</span>
    </div>
    <div class="sb-feed">
      {#each scoreboard.feed as e (e.id)}
        <div class="sb-feed-item" class:fresh={scoreboard.fresh.has(e.id)}>
          <div class="sb-feed-pct">{e.pct}%</div>
          <div class="sb-feed-handles">
            {e.pair[0]}<span class="sb-amp">×</span>{e.pair[1]}
          </div>
          <div class="sb-feed-meta">
            <span class="sb-meta-cat">{e.cat}</span>
            <span>{fmtTime(e.minsAgo)}</span>
          </div>
        </div>
      {/each}
    </div>
  </div>
</section>

<style>
  .scoreboard {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .sb-header {
    display: flex;
    align-items: center;
    gap: 10px;
    /* Sub-header: sits below the main app .header which already absorbs
       the safe-top inset, so use a fixed cushion only. */
    padding: 10px var(--gutter);
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  :global(body.brutal) .sb-header {
    border-bottom-width: 2px;
  }
  .icon-btn {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    background: var(--surface);
    border: 1px solid var(--border);
    display: grid;
    place-items: center;
    transition: var(--transition);
  }
  :global(body.brutal) .icon-btn {
    border-width: 2px;
  }
  .icon-btn:hover {
    background: var(--surface-2);
  }
  .icon-btn svg {
    width: 14px;
    height: 14px;
  }
  .sb-title {
    flex: 1;
    font-weight: 800;
    letter-spacing: 0.1em;
    font-size: 12px;
    text-transform: uppercase;
  }
  .sb-live {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--hot);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.12em;
    text-shadow: 0 0 10px rgba(255, 56, 188, 0.6);
  }
  :global(body.brutal) .sb-live {
    color: var(--text);
    text-shadow: none;
  }
  .sb-live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--hot);
    box-shadow: 0 0 12px var(--hot);
    animation: sbPulseDot 1.5s ease-in-out infinite;
  }
  :global(body.brutal) .sb-live-dot {
    background: var(--text);
    border-radius: 0;
    box-shadow: none;
  }
  @keyframes sbPulseDot {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.35;
      transform: scale(1.4);
    }
  }

  .sb-section-title {
    font-size: 10px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 800;
    padding: 12px var(--gutter) 6px;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
  .sb-side {
    color: var(--text-3);
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: none;
  }

  .sb-top3-section {
    flex-shrink: 0;
    padding-bottom: 6px;
    background: var(--bg);
    z-index: 1;
  }
  .sb-podium {
    padding: 0 var(--gutter);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .sb-podium-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    overflow: hidden;
  }
  :global(body.brutal) .sb-podium-card {
    border-width: 2px;
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .sb-podium-card.rank-1 {
    border-color: rgba(255, 216, 74, 0.6);
    box-shadow: var(--depth-sm) var(--gold-deep), 0 0 30px rgba(255, 216, 74, 0.18);
    background: linear-gradient(90deg, rgba(255, 216, 74, 0.12), rgba(255, 56, 188, 0.04));
  }
  .sb-podium-card.rank-2 {
    border-color: rgba(0, 220, 255, 0.5);
    box-shadow: var(--depth-sm) var(--cyan-deep);
    background: linear-gradient(90deg, rgba(0, 220, 255, 0.08), rgba(167, 139, 250, 0.04));
  }
  .sb-podium-card.rank-3 {
    border-color: rgba(167, 139, 250, 0.5);
    box-shadow: var(--depth-sm) var(--violet-deep);
    background: linear-gradient(90deg, rgba(167, 139, 250, 0.08), transparent);
  }
  :global(body.brutal) .sb-podium-card.rank-1,
  :global(body.brutal) .sb-podium-card.rank-2,
  :global(body.brutal) .sb-podium-card.rank-3 {
    background: var(--bg);
  }
  .sb-rank-num {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: -0.05em;
    width: 24px;
    text-align: center;
    flex-shrink: 0;
    line-height: 1;
  }
  .sb-podium-card.rank-1 .sb-rank-num {
    color: var(--gold);
    text-shadow: 0 0 12px rgba(255, 216, 74, 0.6);
  }
  .sb-podium-card.rank-2 .sb-rank-num {
    color: var(--cyan);
    text-shadow: 0 0 12px rgba(0, 220, 255, 0.5);
  }
  .sb-podium-card.rank-3 .sb-rank-num {
    color: var(--violet);
    text-shadow: 0 0 12px rgba(167, 139, 250, 0.5);
  }
  :global(body.brutal) .sb-rank-num {
    color: var(--text);
  }
  .sb-pair-stack {
    flex: 1;
    min-width: 0;
  }
  .sb-pair-handles {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: var(--text);
    line-height: 1.15;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sb-amp {
    color: var(--text-3);
    margin: 0 4px;
    font-weight: 600;
  }
  .sb-pair-meta {
    font-size: 9px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    margin-top: 3px;
    display: flex;
    gap: 8px;
  }
  .sb-meta-cat {
    color: var(--gold);
  }
  :global(body.brutal) .sb-meta-cat {
    color: var(--text);
  }
  .sb-pct {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: -0.04em;
    background: linear-gradient(135deg, var(--gold), var(--hot));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    flex-shrink: 0;
    line-height: 1;
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
    paint-order: stroke fill;
  }
  :global(body.brutal) .sb-pct {
    background: none;
    -webkit-text-fill-color: var(--text);
    color: var(--text);
  }

  .sb-feed-section {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .sb-feed {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 var(--gutter) max(var(--safe-cta-bottom), 12px);
    display: flex;
    flex-direction: column;
    gap: 5px;
    -webkit-overflow-scrolling: touch;
  }
  .sb-feed-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    flex-shrink: 0;
    transition:
      border-color 600ms ease,
      box-shadow 600ms ease;
    /* Skip rendering offscreen feed rows to keep 60fps when 40+ entries
       arrive on small iPhone screens. */
    content-visibility: auto;
    contain-intrinsic-size: 0 44px;
    contain: layout paint style;
  }
  :global(body.brutal) .sb-feed-item {
    border-width: 2px;
  }
  .sb-feed-item.fresh {
    border-color: var(--hot);
    box-shadow: var(--glow-hot);
    animation: sbFeedItemIn 450ms var(--spring-bouncy);
  }
  :global(body.brutal) .sb-feed-item.fresh {
    box-shadow: 4px 4px 0 0 var(--text);
  }
  @keyframes sbFeedItemIn {
    from {
      transform: translateY(-14px) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
  .sb-feed-pct {
    font-size: 14px;
    font-weight: 900;
    color: var(--gold);
    letter-spacing: -0.02em;
    flex-shrink: 0;
    width: 38px;
    text-align: right;
    text-shadow: 0 0 8px rgba(255, 216, 74, 0.45);
  }
  :global(body.brutal) .sb-feed-pct {
    color: var(--text);
  }
  .sb-feed-handles {
    flex: 1;
    min-width: 0;
    font-size: 11px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text);
  }
  .sb-feed-meta {
    font-size: 9px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    flex-shrink: 0;
    text-align: right;
    line-height: 1.2;
  }
  .sb-feed-meta .sb-meta-cat {
    display: block;
  }
</style>
